import {
  MDBCol,
  MDBContainer,
  MDBIcon,
  MDBModal,
  MDBModalBody,
  MDBModalHeader,
  MDBRow
} from "mdbreact";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { AppContainer } from "../../../containers/App";
import { CampaignContainer } from "../../../containers/Campaign";
import { ErrorContainer } from "../../../containers/Error";
import { PointCardContainer } from "../../../containers/PointCard";
import { Shop, ShopContainer } from "../../../containers/Shops";
import { UserContainer } from "../../../containers/User";
import ShopIcon from "../../elements/shop-icon/ShopIcon";
import ShopDescriptionModal from "../../modules/shop-description-modal/ShopDescriptionModal";
import classes from "./ShopListPage.module.scss";

/**
 * 店舗一覧画面コンポーネント
 */
const ShopListPage: React.FC = () => {
  /** エリア一覧モーダルの開閉状態 */
  const [isAreaListOpen, setIsAreaListOpen] = useState<boolean>(false);
  /** 店舗詳細モーダルに表示する店舗 */
  const [selectedShop, setSelectedShop] = useState<Shop | undefined>(undefined);
  /** 店舗詳細モーダルの表示状態 */
  const [isShopModalOpen, setIsShopModalOpen] = useState<boolean>(false);

  const history = useHistory();
  const appContainer = AppContainer.useContainer();
  const errorContainer = ErrorContainer.useContainer();
  const userContainer = UserContainer.useContainer();
  const shopContainer = ShopContainer.useContainer();
  const campaignContainer = CampaignContainer.useContainer();
  const pointCardContainer = PointCardContainer.useContainer();

  const user = userContainer.user;
  const userId = user.id || user.rawId;

  const campaign = campaignContainer.campaign;
  const campaignId = campaignContainer.campaignId;
  const isReached = campaignContainer.isReached;

  const pointCard = pointCardContainer.pointCard;

  useEffect(() => {
    // 未エントリーの場合はエントリーページに遷移
    if (campaign && pointCard && !pointCard.isEntry) {
      history.push(`/${campaignId}/${user.rawId}`);
    }

    // キャンペーン達成者数が上限に達した場合 または 達成者数が不明の場合は
    // ポイントカード画面に遷移
    if (isReached || isReached === undefined) {
      history.push(`/${campaignId}/point-card/${user.rawId}`);
    }
  }, [history, campaign, campaignId, pointCard, isReached, user.rawId]);

  const shops = shopContainer.shops;
  if (!shops && !appContainer.context.loading) {
    // 店舗一覧を初期化
    appContainer.setLoading(true);
    shopContainer
      .getShopList(campaignId)
      .then(() => {
        appContainer.setLoading(false);
      })
      .catch((error) => {
        errorContainer.setDefaultError(userId, error);
      });
  }

  /**
   * 戻るボタン押下時の処理
   */
  const handleReturnClick = () => {
    history.push(`/${campaignId}/point-card/${user.rawId}`);
  };

  /**
   * フロアの表示コンテンツ
   * @param floorIdx フロアのインデックス
   * @param floorName フロア名
   * @param shops フロアに属する店舗のリスト
   */
  const floorBlock = (floorIdx: number, floorName: string, shops: Shop[]) => {
    return (
      <div
        key={`floor-${floorIdx}`}
        className={classes.floorBlock}
        id={`floor-${floorIdx}`}
      >
        <div className={classes.floorName}>{floorName}</div>
        {shopTile(shops)}
      </div>
    );
  };

  /**
   * 店舗のタイル表示コンテンツ
   * @param shops タイル表示する店舗のリスト
   */
  const shopTile = (shops: Shop[]) => {
    return (
      <div>
        <MDBRow className="mx-0">
          {shops.map((shop, shopIdx) => {
            return (
              <MDBCol key={`shop-${shopIdx}`} size="3" className="px-1">
                <ShopIcon
                  image={shop.image}
                  name={shop.name}
                  onClick={() => {
                    setSelectedShop(shop);
                    setIsShopModalOpen(true);
                  }}
                ></ShopIcon>
              </MDBCol>
            );
          })}
        </MDBRow>
      </div>
    );
  };

  return (
    <React.Fragment>
      <MDBContainer
        className={`${classes.container} d-flex flex-column py-3`}
        fluid
      >
        <div className={classes.header}>
          <div className={classes.contentTitle}>キャンペーン対象店舗一覧</div>
          <div className="my-3 d-flex justify-content-between">
            <button
              type="button"
              className={classes.button}
              onClick={handleReturnClick}
            >
              戻る
            </button>
            <button
              type="button"
              className={classes.button}
              onClick={() => {
                setIsAreaListOpen(true);
              }}
            >
              フロア一覧
            </button>
          </div>
        </div>

        <div className={`${classes.body} ${classes.scroll}`}>
          {campaign?.setting.floors && shops ? (
            <React.Fragment>
              {campaign.setting.floors.map((floor, floorIdx) => {
                const targetShops = shops.filter(
                  (shop) => shop.floor === floor
                );
                return floorBlock(floorIdx, floor, targetShops);
              })}
            </React.Fragment>
          ) : null}
        </div>
      </MDBContainer>

      <MDBModal
        className={classes.areaSelectModal}
        isOpen={isAreaListOpen}
        toggle={() => {
          setIsAreaListOpen(false);
        }}
        position="right"
      >
        <MDBModalHeader>
          <div className={classes.title}>フロア一覧</div>
        </MDBModalHeader>
        <MDBModalBody>
          {campaign?.setting.floors.map((floor, floorIdx) => {
            return (
              <div key={floorIdx} className={classes.areaLink}>
                <a
                  className="d-block"
                  href={`#floor-${floorIdx}`}
                  onClick={() => {
                    setIsAreaListOpen(false);
                  }}
                >
                  <MDBIcon icon="angle-left" className="mr-2" />
                  {floor}
                </a>
              </div>
            );
          })}
        </MDBModalBody>
      </MDBModal>

      <ShopDescriptionModal
        isOpen={isShopModalOpen}
        shop={selectedShop}
        notices={campaign?.notices.shopDescription}
        onCloseButtonClick={() => {
          setIsShopModalOpen(false);
        }}
      ></ShopDescriptionModal>
    </React.Fragment>
  );
};

export default ShopListPage;
